import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCreateCampaign, useUpdateCampaign, useCampaign } from '../hooks/useCampaigns';
import {
  SchedulingType,
  AudienceType,
} from '@troia-v3/types';
import type {
  CreateCampaignRequest,
  LeadResponse,
  ContactResponse,
  TemplateResponse,
} from '@troia-v3/types';
import { TemplatePreview } from '@/modules/templates/components/TemplatePreview';
import { renderTemplateSeguro, valoresDeExemplo } from '@/modules/templates/components/templateRender';
import { FormSection, Select, Tabs, RadioGroup, Checkbox, Input, Textarea } from '@/shared/components/ui';
import { VariableMappingEditor } from '../components/VariableMappingEditor';
import type { TabItem } from '@/shared/components/ui/Tabs';
import { channelsApi } from '@/modules/channels/services';
import type { ChannelDropdownItem } from '@/modules/channels/services/channelsApi';
import {
  getContactIdentifierForProvider,
  identifierTypeForCampaignAudience,
  providerHasContactRule,
} from '@/shared/utils/channelCompatibility';
import { templateService } from '@/modules/templates/services';
import { useCampaignAudience, useCampaignAudienceTags } from '../hooks/useCampaignAudience';
import { useAudiencesDropdown } from '@/modules/audiences/hooks/useAudiencesDropdown';
import { useAudience } from '@/modules/audiences/hooks/useAudiences';
import type { AudienceIdentifierType } from '@troia-v3/types';
import { AdvancedFiltersBar } from '@/shared/components/ui/AdvancedFiltersBar';
import { ListPagePagination } from '@/shared/components/ui/ListPagePagination';
import {
  LEADS_FILTER_DEFINITIONS,
  LEADS_FILTER_CATEGORIES,
  CONTACTS_FILTER_DEFINITIONS,
  CONTACTS_FILTER_CATEGORIES,
} from '../components/CampaignAudienceFilterDefinitions';
import type { FilterOption } from '@/shared/components/ui/AdvancedFiltersBar';
import { useLeadsCampaignsDropdown, useLeadsAdsetsDropdown, useLeadsAdsDropdown, useLeadsOriginsDropdown, useLeadsTagsDropdown, useLeadsTypesDropdown } from '@/modules/crm/leads/hooks/useLeadsDropdowns';
import { useApiError } from '@/hooks/useApiError';
import { toast } from 'sonner';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
/**
 * Converte datetime-local (horário LOCAL do usuário, sem fuso) para ISO 8601 UTC.
 *
 * O input `datetime-local` devolve um horário "de parede" sem timezone
 * (ex: "2025-11-15T22:02"). `new Date(...)` o interpreta no fuso do browser e
 * `toISOString()` converte para UTC — mesmo padrão do calendário
 * (EventFormModal `toUTCDateTimeString`) e dos leads (WinLeadDialog). O bug
 * anterior colava ":00.000Z" no horário local, tratando-o como UTC, o que
 * disparava a campanha no horário errado (deslocado pelo offset do usuário).
 */
const convertToISO8601 = (datetimeLocal: string): string => {
  if (!datetimeLocal) return '';
  return new Date(datetimeLocal).toISOString();
};

/**
 * Converte ISO 8601 UTC para datetime-local (horário LOCAL do usuário).
 *
 * Subtrai o offset do browser para mostrar o horário de parede local no input
 * (mesmo padrão do calendário `toLocalDateTimeString`).
 */
const convertFromISO8601 = (iso8601: string): string => {
  if (!iso8601) return '';
  const date = new Date(iso8601);
  const localMs = date.getTime() - date.getTimezoneOffset() * 60000;
  return new Date(localMs).toISOString().slice(0, 16);
};

/**
 * Campaign Form Page
 * Multi-step wizard for creating/editing campaigns
 */
export const CampaignFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = !!id;

  const { data: existingCampaign, isLoading: isLoadingCampaign } = useCampaign(id);
  const isReadOnly = isEditMode && (existingCampaign?.status === 'completed' || existingCampaign?.status === 'failed');
  const createCampaign = useCreateCampaign();
  const updateCampaign = useUpdateCampaign();

  const { handleApiFieldError } = useApiError();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState(1);
  const [channels, setChannels] = useState<ChannelDropdownItem[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [formData, setFormData] = useState<Partial<CreateCampaignRequest>>({
    name: '',
    description: '',
    channelId: '',
    templateId: '',
    audienceType: AudienceType.LEADS,
    recipientIds: [],
    audienceId: undefined,
    variableMapping: {},
    schedulingType: SchedulingType.IMMEDIATE,
  });

  // Load channels on mount
  useEffect(() => {
    const loadChannels = async () => {
      setLoadingChannels(true);
      try {
        const data = await channelsApi.getForDropdown();
        setChannels(data);
      } catch (error) {
        console.error('Erro ao carregar channels:', error);
        setChannels([]);
      } finally {
        setLoadingChannels(false);
      }
    };

    loadChannels();
  }, []);

  // Pré-seleciona o público quando vem de "Usar em campanha" (?audienceId=) — só no create
  useEffect(() => {
    if (isEditMode) return;
    const preAudienceId = searchParams.get('audienceId');
    if (preAudienceId) {
      setFormData((prev) => ({ ...prev, audienceType: AudienceType.CUSTOM_AUDIENCE, audienceId: preAudienceId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Populate form when editing existing campaign
  useEffect(() => {
    if (existingCampaign && isEditMode) {
      setFormData({
        name: existingCampaign.name || '',
        description: existingCampaign.description || '',
        channelId: existingCampaign.channelId || '',
        templateId: existingCampaign.templateId || '',
        audienceType: existingCampaign.audienceType || AudienceType.LEADS,
        recipientIds: existingCampaign.recipientIds || [],
        audienceId: existingCampaign.audienceId,
        variableMapping: existingCampaign.variableMapping || {},
        schedulingType: existingCampaign.schedulingType || SchedulingType.IMMEDIATE,
        // Converter scheduledFor de ISO 8601 para datetime-local
        scheduledFor: existingCampaign.scheduledFor
          ? convertFromISO8601(existingCampaign.scheduledFor)
          : undefined,
        recurringConfig: existingCampaign.recurringConfig,
      });
    }
  }, [existingCampaign, isEditMode]);

  const steps: Array<{ id: number; name: string; icon: IconName }> = [
    { id: 1, name: 'Informações', icon: 'file-text' },
    { id: 2, name: 'Público-Alvo', icon: 'users' },
    { id: 3, name: 'Conteúdo', icon: 'message-square' },
    { id: 4, name: 'Agendamento', icon: 'clock' },
    { id: 5, name: 'Revisão', icon: 'check-circle' },
  ];

  const handleSave = async () => {
    try {
      setFieldErrors({});

      // Derive audienceType defensively — if selection flow left it undefined
      // infer it from the selected recipient pointer (audienceId → CUSTOM_AUDIENCE,
      // otherwise fall back to the form state or LEADS as last resort).
      const derivedAudienceType =
        formData.audienceType ??
        (formData.audienceId ? AudienceType.CUSTOM_AUDIENCE : AudienceType.LEADS);

      // Also ensure the counterpart field is cleared according to the audience type
      // so the backend's discriminated-union validation is happy.
      const recipientIdsForSubmit =
        derivedAudienceType === AudienceType.CUSTOM_AUDIENCE ? [] : formData.recipientIds ?? [];
      const audienceIdForSubmit =
        derivedAudienceType === AudienceType.CUSTOM_AUDIENCE ? formData.audienceId : undefined;

      // Sanitizar scheduledFor antes de enviar
      // Quando "Enviar agora" é selecionado, enviamos null para limpar o campo no backend
      const sanitizedData = {
        ...formData,
        audienceType: derivedAudienceType,
        recipientIds: recipientIdsForSubmit,
        audienceId: audienceIdForSubmit,
        scheduledFor: formData.schedulingType === SchedulingType.SCHEDULED && formData.scheduledFor
          ? convertToISO8601(formData.scheduledFor)
          : null  // Explicitamente null para limpar o campo
      };

      if (isEditMode && id) {
        await updateCampaign.mutateAsync({
          id,
          data: sanitizedData,
        });
      } else {
        await createCampaign.mutateAsync(sanitizedData as CreateCampaignRequest);
      }
      toast.success('Campanha salva com sucesso!');
      navigate('/prospecting/campaigns');
    } catch (error) {
      const hasFieldErrors = handleApiFieldError(error, setFieldErrors);
      if (hasFieldErrors) {
        toast.error('Erro de validação. Verifique os campos destacados.');
      }
    }
  };

  const updateFormData = useCallback((updates: Partial<CreateCampaignRequest>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const isSaving = createCampaign.isPending || updateCampaign.isPending;
  const pageTitle = isEditMode ? (existingCampaign?.name || 'Editar campanha') : 'Nova campanha';
  const saveLabel = isEditMode ? 'Salvar' : 'Criar campanha';
  const savingLabel = isEditMode ? 'Salvando...' : 'Criando...';

  if (isLoadingCampaign) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <Icon name="loader" className="w-8 h-8 animate-spin" style={{ color: 'var(--theme-button-primary)' }} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Sticky Header: breadcrumb + actions ──────────────── */}
      <div
        className="flex-shrink-0 flex items-center gap-3 px-4 py-2.5"
        style={{ backgroundColor: 'var(--theme-card-background)', borderBottom: '1px solid var(--theme-card-border)' }}
      >
        {/* Breadcrumb (nunca seta-voltar) */}
        <nav className="flex items-center gap-1.5 min-w-0 flex-1" aria-label="breadcrumb">
          <button
            onClick={() => navigate('/prospecting/campaigns')}
            className="text-sm font-medium whitespace-nowrap transition-colors hover:opacity-80"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Prospecção
          </button>
          <Icon name="chevron-right" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
          <button
            onClick={() => navigate('/prospecting/campaigns')}
            className="text-sm font-medium whitespace-nowrap transition-colors hover:opacity-80"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Campanhas
          </button>
          <Icon name="chevron-right" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
          <span className="text-base font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>
            {pageTitle}
          </span>
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isReadOnly && (
            <span className="hidden md:inline text-sm font-medium whitespace-nowrap" style={{ color: 'var(--theme-warning-color)' }}>
              {isEditMode ? 'Alterações não salvas' : 'Preencha os campos obrigatórios'}
            </span>
          )}
          <button
            data-testid="campaign-form-cancel"
            onClick={() => navigate('/prospecting/campaigns')}
            className={`${isReadOnly ? 'inline-flex' : 'hidden sm:inline-flex'} items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors`}
            style={{
              backgroundColor: 'transparent',
              borderColor: 'var(--theme-card-border)',
              color: 'var(--theme-card-text)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {isReadOnly ? 'Voltar' : 'Cancelar'}
          </button>
          {!isReadOnly && (
            <button
              data-testid="campaign-form-save"
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-50 flex-shrink-0"
              style={{ backgroundColor: 'var(--theme-button-primary)' }}
              onMouseEnter={(e) => { if (!isSaving) e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              {isSaving ? (
                <>
                  <Icon name="loader" className="w-4 h-4 animate-spin" />
                  {savingLabel}
                </>
              ) : (
                <>
                  <Icon name="check" className="w-4 h-4" />
                  {saveLabel}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* ── Abas do wizard (shared Tabs pageLevel — F55) ─────── */}
      {(() => {
        const stepBody = (content: React.ReactNode) => (
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
            {/* Validation Errors */}
            {Object.keys(fieldErrors).length > 0 && (
              <div
                data-testid="campaign-form-errors"
                className="mb-4 p-4 rounded-lg border"
                style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.07)',
                  borderColor: 'rgba(239, 68, 68, 0.3)',
                }}
              >
                <div className="flex items-center gap-2">
                  <Icon name="alert-triangle" className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--theme-error-color)' }} />
                  <p className="text-sm font-bold" style={{ color: 'var(--theme-error-color)' }}>
                    Corrija os erros abaixo antes de salvar
                  </p>
                </div>
                <ul className="mt-2 pl-6 list-disc space-y-0.5">
                  {Object.entries(fieldErrors).map(([field, message]) => (
                    <li key={field} className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
                      <span className="font-semibold">{field}:</span> {message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {content}
          </div>
        );

        const stepContent: Record<number, React.ReactNode> = {
          1: (
            <StepBasicInfo
              formData={formData}
              updateFormData={updateFormData}
              channels={channels}
              loadingChannels={loadingChannels}
            />
          ),
          2: <StepAudience formData={formData} updateFormData={updateFormData} channels={channels} />,
          3: <StepContent formData={formData} updateFormData={updateFormData} />,
          4: <StepSchedule formData={formData} updateFormData={updateFormData} />,
          5: <StepReview formData={formData} channels={channels} />,
        };

        const stepTabs: TabItem[] = steps.map((step) => ({
          id: String(step.id),
          label: step.name,
          icon: step.icon,
          content: stepBody(stepContent[step.id]),
          'data-testid': `campaign-step-${step.id}`,
        }));

        return (
          <Tabs
            variant="underline"
            pageLevel
            fullBleed
            activeTab={String(currentStep)}
            onChange={(t) => setCurrentStep(Number(t))}
            tabs={stepTabs}
          />
        );
      })()}
    </div>
  );
};

/**
 * Step 1: Basic Info
 */
interface StepProps {
  formData: Partial<CreateCampaignRequest>;
  updateFormData: (updates: Partial<CreateCampaignRequest>) => void;
}

interface StepBasicInfoProps extends StepProps {
  channels: Array<{ id: string; name: string }>;
  loadingChannels: boolean;
}

const StepBasicInfo: React.FC<StepBasicInfoProps> = ({ formData, updateFormData, channels, loadingChannels }) => {
  return (
    <FormSection
      icon={'file-text'}
      title="Informações Básicas"
      description="Nome, descrição e canal de envio da campanha"
    >
      <div>
        <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-card-text)' }}>
          Nome da Campanha <span style={{ color: 'var(--theme-error-color)' }}>*</span>
        </label>
        <Input
          data-testid="campaign-name-input"
          type="text"
          value={formData.name || ''}
          onChange={(e) => updateFormData({ name: e.target.value })}
          placeholder="Ex: Promoção Black Friday 2024"
        />
      </div>

      <Textarea
        data-testid="campaign-description-input"
        label="Descrição"
        value={formData.description || ''}
        onChange={(e) => updateFormData({ description: e.target.value })}
        placeholder="Objetivo e contexto desta campanha"
        rows={3}
      />

      <Select
        data-testid="campaign-channel-select"
        label="Canal de Comunicação"
        required
        placeholder={loadingChannels ? 'Carregando canais...' : 'Selecione um canal'}
        options={channels.map((channel) => ({
          value: channel.id,
          label: channel.name,
        }))}
        value={formData.channelId || ''}
        onChange={(val) => updateFormData({ channelId: val })}
        disabled={loadingChannels}
        description={
          !loadingChannels && channels.length === 0
            ? 'Nenhum canal disponivel. Crie um canal primeiro.'
            : 'O canal define os templates e o tipo de identificador do público'
        }
      />
    </FormSection>
  );
};

/**
 * Step 2: Audience
 * Server-side search, pagination, advanced filters, and tag display
 */
interface StepAudienceProps extends StepProps {
  channels: ChannelDropdownItem[];
}

const StepAudience: React.FC<StepAudienceProps> = ({ formData, updateFormData, channels }) => {
  const [activeTab, setActiveTab] = useState<AudienceType>(
    (formData.audienceType as AudienceType) === AudienceType.CONTACTS
      ? AudienceType.CONTACTS
      : (formData.audienceType as AudienceType) === AudienceType.CUSTOM_AUDIENCE
      ? AudienceType.CUSTOM_AUDIENCE
      : AudienceType.LEADS
  );

  // Provider do canal escolhido no passo 1 — decide o identificador mostrado
  // embaixo do nome e se o canal sabe alcançar contatos (regra igual à da modal
  // "Iniciar atendimento", em `shared/utils/channelCompatibility`).
  const selectedChannel = useMemo(
    () => (formData.channelId ? channels.find((c) => c.id === formData.channelId) : undefined),
    [channels, formData.channelId],
  );
  const providerId = selectedChannel?.providerId;
  const channelWithoutRule = !!selectedChannel && !providerHasContactRule(providerId);

  const audienceIdentifierType: AudienceIdentifierType | undefined = useMemo(
    () => identifierTypeForCampaignAudience(providerId),
    [providerId],
  );

  const { data: audiencesDropdown, isLoading: loadingAudiences } = useAudiencesDropdown(
    audienceIdentifierType,
    { enabled: activeTab === AudienceType.CUSTOM_AUDIENCE && !!audienceIdentifierType }
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(formData.recipientIds || [])
  );
  const ITEMS_PER_PAGE = 20;

  // Ref for stable callback
  const updateFormDataRef = useRef(updateFormData);
  updateFormDataRef.current = updateFormData;

  // Debounce search (400ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // reset page on search change
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  // Clear selection when channel changes (contacts may no longer be compatible)
  const prevChannelIdRef = useRef(formData.channelId);
  useEffect(() => {
    if (prevChannelIdRef.current !== formData.channelId) {
      prevChannelIdRef.current = formData.channelId;
      setSelectedIds(new Set());
      setPage(1);
    }
  }, [formData.channelId]);

  // React Query: server-side data (filtered by selected channel's provider)
  // Cast: hook expects 'leads' | 'contacts'. The CUSTOM_AUDIENCE branch is
  // rendered separately (Task 5.7) so this hook is only meaningful for leads/contacts.
  const { data, isLoading } = useCampaignAudience(activeTab as 'leads' | 'contacts', {
    page,
    limit: ITEMS_PER_PAGE,
    search: debouncedSearch,
    channelId: formData.channelId || undefined,
    filters,
  });

  const items = data?.items || [];
  const totalPages = data?.totalPages || 1;

  // Dynamic dropdown options for leads filters
  const { data: campaignNames } = useLeadsCampaignsDropdown();
  const { data: adsetNames } = useLeadsAdsetsDropdown();
  const { data: adNames } = useLeadsAdsDropdown();
  const { data: originNames } = useLeadsOriginsDropdown();
  const { data: tagNames } = useLeadsTagsDropdown();
  const { data: typeNames } = useLeadsTypesDropdown();
  const { data: audienceTags } = useCampaignAudienceTags();

  // Build dynamic options map for AdvancedFiltersBar
  const leadsDynamicOptions = useMemo<Record<string, FilterOption[]>>(() => {
    const toOptions = (names: string[] | undefined): FilterOption[] =>
      (names || []).map(n => ({ value: n, label: n }));
    return {
      campaignName: toOptions(campaignNames),
      adsetName: toOptions(adsetNames),
      adName: toOptions(adNames),
      origin: toOptions(originNames),
      type: toOptions(typeNames),
      tags: toOptions(tagNames),
    };
  }, [campaignNames, adsetNames, adNames, originNames, tagNames, typeNames]);

  const contactsDynamicOptions = useMemo<Record<string, FilterOption[]>>(() => {
    return {
      tags: (audienceTags || []).map(t => ({ value: t, label: t })),
    };
  }, [audienceTags]);

  // Sync formData when selection or tab changes
  // For CUSTOM_AUDIENCE, selection state is managed via audienceId (handled in branch below)
  useEffect(() => {
    if (activeTab === AudienceType.CUSTOM_AUDIENCE) {
      updateFormDataRef.current({ audienceType: activeTab });
      return;
    }
    updateFormDataRef.current({
      audienceType: activeTab,
      recipientIds: Array.from(selectedIds),
    });
  }, [activeTab, selectedIds]);

  // Tab switch: clear selection and filters and search
  const handleTabChange = (tabId: string) => {
    const newTab = tabId as AudienceType;
    if (newTab === activeTab) return;
    setActiveTab(newTab);
    setSelectedIds(new Set());
    setSearchTerm('');
    setDebouncedSearch('');
    setFilters({});
    setPage(1);
    // Clear fields not relevant for the new tab
    if (newTab === AudienceType.CUSTOM_AUDIENCE) {
      updateFormDataRef.current({ recipientIds: [] });
    } else {
      updateFormDataRef.current({ audienceId: undefined });
    }
  };

  // Toggle selection
  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Select all on current page
  const selectAllPage = () => {
    const newSelected = new Set(selectedIds);
    if (activeTab === AudienceType.LEADS) {
      (items as LeadResponse[]).forEach(lead => {
        if (lead.contactId) newSelected.add(lead.contactId);
      });
    } else {
      (items as ContactResponse[]).forEach(contact => {
        newSelected.add(contact.id);
      });
    }
    setSelectedIds(newSelected);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // Render tag chips with +N overflow
  const renderTags = (tags: string[], max: number) => {
    if (!tags || tags.length === 0) return null;
    const visible = tags.slice(0, max);
    const remaining = tags.length - max;
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {visible.map(tag => (
          <span
            key={tag}
            className="text-xs px-2 py-0.5 rounded-full font-medium"
            style={{
              backgroundColor: 'var(--theme-button-ghost-hover)',
              color: 'var(--theme-card-text)',
            }}
          >
            {tag}
          </span>
        ))}
        {remaining > 0 && (
          <span
            className="text-xs px-1.5 py-0.5 rounded-full"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            +{remaining}
          </span>
        )}
      </div>
    );
  };

  // Current filter definitions and dynamic options based on active tab
  const currentDefinitions = activeTab === AudienceType.LEADS
    ? LEADS_FILTER_DEFINITIONS
    : CONTACTS_FILTER_DEFINITIONS;
  const currentCategories = activeTab === AudienceType.LEADS
    ? LEADS_FILTER_CATEGORIES
    : CONTACTS_FILTER_CATEGORIES;
  const currentDynamicOptions = activeTab === AudienceType.LEADS
    ? leadsDynamicOptions
    : contactsDynamicOptions;

  const audienceTabs: TabItem[] = [
    {
      id: AudienceType.LEADS,
      label: 'Leads',
      icon: 'user-check',
      badge: activeTab === AudienceType.LEADS && selectedIds.size > 0 ? selectedIds.size : undefined,
      content: null,
    },
    {
      id: AudienceType.CONTACTS,
      label: 'Contatos',
      icon: 'user',
      badge: activeTab === AudienceType.CONTACTS && selectedIds.size > 0 ? selectedIds.size : undefined,
      content: null,
    },
    {
      id: AudienceType.CUSTOM_AUDIENCE,
      label: 'Públicos',
      icon: 'list',
      content: null,
    },
  ];

  return (
    <FormSection
      icon={'users'}
      title="Público-Alvo"
      description="Quem receberá esta campanha"
    >
      {/* Tabs */}
      <Tabs
        tabs={audienceTabs}
        activeTab={activeTab}
        onChange={handleTabChange}
        variant="pills"
      />

      {activeTab !== AudienceType.CUSTOM_AUDIENCE && (<>
      {/* Search + Select All */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-4">
        <div className="flex-1">
          <Input
            data-testid="campaign-audience-search"
            type="text"
            icon="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={`Buscar ${activeTab === AudienceType.LEADS ? 'leads' : 'contatos'}...`}
          />
        </div>
        <button
          type="button"
          data-testid="campaign-audience-select-all"
          onClick={selectAllPage}
          className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors hover:opacity-80 flex-shrink-0"
          style={{
            backgroundColor: 'transparent',
            borderColor: 'var(--theme-card-border)',
            color: 'var(--theme-card-text)',
          }}
        >
          Selecionar todos
        </button>
      </div>

      {/* Advanced Filters */}
      <div>
        <AdvancedFiltersBar
          definitions={currentDefinitions}
          categories={currentCategories}
          filters={filters}
          onFiltersChange={setFilters}
          dynamicOptions={currentDynamicOptions}
        />
      </div>

      {/* List — sem caixa aninhada (itens correm no card-body; scroll só p/ usabilidade em listas longas) */}
      <div className="max-h-96 overflow-y-auto">
        {channelWithoutRule ? (
          <div className="p-8 text-center" data-testid="campaign-audience-channel-without-rule">
            <p style={{ color: 'var(--theme-muted-text)' }}>
              Este canal não permite selecionar destinatários. Escolha um canal de e-mail ou de mensagens no passo Informações.
            </p>
          </div>
        ) : isLoading ? (
          <div className="p-8 text-center">
            <p style={{ color: 'var(--theme-muted-text)' }}>Carregando...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center">
            <p style={{ color: 'var(--theme-muted-text)' }}>
              {debouncedSearch || Object.keys(filters).length > 0
                ? `Nenhum ${activeTab === AudienceType.LEADS ? 'lead' : 'contato'} encontrado com os filtros atuais`
                : `Nenhum ${activeTab === AudienceType.LEADS ? 'lead' : 'contato'} encontrado`
              }
            </p>
          </div>
        ) : activeTab === AudienceType.LEADS ? (
          (items as LeadResponse[]).map((lead, index) => {
            const isSelected = !!(lead.contactId && selectedIds.has(lead.contactId));
            return (
              <div
                key={lead.id}
                data-testid={`campaign-recipient-${lead.id}`}
                className="flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors"
                style={{
                  backgroundColor: isSelected ? 'var(--theme-primary-background)' : 'transparent',
                  borderTop: index > 0 ? '1px solid var(--theme-card-border)' : 'none',
                }}
                onClick={() => lead.contactId && toggleSelection(lead.contactId)}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => lead.contactId && toggleSelection(lead.contactId)}
                />
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm font-semibold truncate"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {lead.contact?.name || 'Sem nome'}
                  </p>
                  <p
                    className="text-xs truncate"
                    style={{ color: 'var(--theme-muted-text)' }}
                    data-testid="campaign-recipient-identifier"
                  >
                    {getContactIdentifierForProvider(providerId, lead.contact?.identifiers) || 'Sem contato'}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {lead.segment && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{
                        backgroundColor: 'var(--theme-info-background)',
                        color: 'var(--theme-info-color)',
                      }}
                    >
                      {lead.segment}
                    </span>
                  )}
                  {renderTags(lead.contact?.tags || [], 2)}
                </div>
              </div>
            );
          })
        ) : (
          (items as ContactResponse[]).map((contact, index) => {
            const isSelected = selectedIds.has(contact.id);
            return (
              <div
                key={contact.id}
                data-testid={`campaign-recipient-${contact.id}`}
                className="flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors"
                style={{
                  backgroundColor: isSelected ? 'var(--theme-primary-background)' : 'transparent',
                  borderTop: index > 0 ? '1px solid var(--theme-card-border)' : 'none',
                }}
                onClick={() => toggleSelection(contact.id)}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => toggleSelection(contact.id)}
                />
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm font-semibold truncate"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {contact.name || 'Sem nome'}
                  </p>
                  <p
                    className="text-xs truncate"
                    style={{ color: 'var(--theme-muted-text)' }}
                    data-testid="campaign-recipient-identifier"
                  >
                    {getContactIdentifierForProvider(providerId, contact.identifiers) || 'Sem contato'}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {renderTags(contact.tags || [], 3)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination — F48: "Mostrando X–Y de N" */}
      <ListPagePagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        showPageInfo
        totalItems={data?.total ?? 0}
        pageSize={ITEMS_PER_PAGE}
        itemLabel={activeTab === AudienceType.LEADS ? 'leads' : 'contatos'}
      />

      {/* Selected counter */}
      <div
        className="flex items-center justify-between pt-3"
        style={{ borderTop: '1px solid var(--theme-card-border)' }}
      >
        <span className="text-sm font-bold" style={{ color: 'var(--theme-primary)' }}>
          {selectedIds.size} destinatário{selectedIds.size !== 1 ? 's' : ''} selecionado{selectedIds.size !== 1 ? 's' : ''}
        </span>
        {selectedIds.size > 0 && (
          <button
            type="button"
            data-testid="campaign-audience-clear"
            onClick={clearSelection}
            className="text-sm font-medium px-3 py-1 rounded hover:opacity-80 transition-opacity"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Limpar seleção
          </button>
        )}
      </div>
      </>)}

      {activeTab === AudienceType.CUSTOM_AUDIENCE && (
        <div className="mt-4 space-y-4">
          {!formData.channelId ? (
            <div
              className="rounded-lg border p-4"
              style={{
                backgroundColor: 'var(--theme-warning-background)',
                borderColor: 'var(--theme-warning-color)',
              }}
            >
              <p className="text-sm" style={{ color: 'var(--theme-warning-color)' }}>
                Selecione um canal na etapa anterior para ver os públicos disponíveis.
              </p>
            </div>
          ) : !audienceIdentifierType ? (
            <p className="text-sm py-2" style={{ color: 'var(--theme-muted-text)' }}>
              Não foi possível determinar o tipo de identificador do canal selecionado.
            </p>
          ) : loadingAudiences ? (
            <p className="text-sm py-2" style={{ color: 'var(--theme-muted-text)' }}>
              Carregando públicos...
            </p>
          ) : !audiencesDropdown || audiencesDropdown.length === 0 ? (
            <div
              className="rounded-lg border p-6 text-center"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                borderColor: 'var(--theme-card-border)',
              }}
            >
              <p className="text-sm mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                Nenhum público do tipo{' '}
                <strong>{audienceIdentifierType === 'email' ? 'email' : 'telefone'}</strong> encontrado.
              </p>
              <a
                href="/prospecting/audiences"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm hover:underline"
                style={{ color: 'var(--theme-primary)' }}
              >
                Criar um novo público →
              </a>
            </div>
          ) : (
            <>
              <Select
                data-testid="campaign-audience-select"
                label="Público"
                required
                placeholder="Selecione um público"
                value={formData.audienceId ?? ''}
                onChange={(val) =>
                  updateFormData({
                    audienceType: AudienceType.CUSTOM_AUDIENCE,
                    recipientIds: [],
                    audienceId: val,
                  })
                }
                description="Somente públicos com identificador compatível com o canal"
                options={audiencesDropdown.map((a) => ({
                  value: a.id,
                  label: `${a.name} (${a.totalEntries} entrada${a.totalEntries !== 1 ? 's' : ''})`,
                }))}
              />
              {formData.audienceId && (() => {
                const selected = audiencesDropdown.find((a) => a.id === formData.audienceId);
                if (!selected) return null;
                return (
                  <div
                    className="rounded-lg border p-4 flex items-start gap-3"
                    style={{
                      backgroundColor: 'var(--theme-button-ghost-hover)',
                      borderColor: 'var(--theme-card-border)',
                    }}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
                    >
                      <Icon name="list" className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold" style={{ color: 'var(--theme-card-text)' }}>
                        {selected.name}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                        {selected.totalEntries} destinatário
                        {selected.totalEntries !== 1 ? 's' : ''} ·{' '}
                        Tipo: {selected.identifierType === 'email' ? 'Email' : 'Telefone'}
                      </p>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}
    </FormSection>
  );
};

/**
 * Step 3: Content
 */
const StepContent: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const [templates, setTemplates] = useState<Array<{ id: string; name: string; channelId: string }>>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateResponse | null>(null);

  // Carregar templates quando canal mudar
  useEffect(() => {
    const loadTemplates = async () => {
      if (!formData.channelId) return;

      setLoadingTemplates(true);
      try {
        const data = await templateService.getForDropdown(formData.channelId);
        setTemplates(data);
      } catch (error) {
        console.error('Erro ao carregar templates:', error);
        setTemplates([]);
      } finally {
        setLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, [formData.channelId]);

  // Carregar template selecionado para preview e popular variableMapping padrão
  useEffect(() => {
    const loadTemplateDetails = async () => {
      if (!formData.templateId) {
        setSelectedTemplate(null);
        return;
      }

      try {
        const template = await templateService.getById(formData.templateId);
        setSelectedTemplate(template);

        // Auto-populate default mapping: all variables default to 'name' field
        if (template.variables && template.variables.length > 0) {
          const defaultMapping: Record<number, { source: 'field' | 'static'; fieldName?: string; staticValue?: string }> = {};
          for (const variable of template.variables) {
            // Only set default if not already configured
            if (!formData.variableMapping?.[variable.position]) {
              defaultMapping[variable.position] = { source: 'field', fieldName: 'name' };
            }
          }
          if (Object.keys(defaultMapping).length > 0) {
            updateFormData({
              variableMapping: { ...formData.variableMapping, ...defaultMapping },
            });
          }
        }
      } catch (error) {
        console.error('Erro ao carregar template:', error);
        setSelectedTemplate(null);
      }
    };

    loadTemplateDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.templateId]);

  return (
    <FormSection
      icon={'message-square'}
      title="Conteúdo da Mensagem"
      description="Template aprovado do canal selecionado"
    >
      {/* Template Selection */}
      {!formData.channelId ? (
        <p className="text-sm py-2" style={{ color: 'var(--theme-muted-text)' }}>
          Selecione um canal na etapa anterior para ver os templates disponíveis
        </p>
      ) : (
        <Select
          data-testid="campaign-template-select"
          label="Template"
          required
          placeholder={loadingTemplates ? 'Carregando templates...' : 'Selecione um template'}
          options={templates.map((template) => ({
            value: template.id,
            label: template.name,
          }))}
          value={formData.templateId || ''}
          onChange={(val) => updateFormData({ templateId: val })}
          disabled={loadingTemplates}
          description={
            !loadingTemplates && templates.length === 0
              ? 'Nenhum template aprovado disponivel para este canal'
              : 'Somente templates aprovados disponíveis para este canal'
          }
        />
      )}

      {/* Pré-visualização — mesmo renderizador do chat e da tela de criação.
          Os valores reais vêm de cada destinatário na hora do disparo; aqui a
          tela mostra o exemplo declarado no template (e `[Rótulo]` quando não
          há exemplo). Nunca mais `{{1}}` cru. */}
      {selectedTemplate && (
        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-card-text)' }}>
            Pré-visualização
          </label>
          <div
            className="p-4 rounded-lg border"
            style={{
              backgroundColor: 'var(--theme-input-background)',
              borderColor: 'var(--theme-input-border)',
            }}
            data-testid="campaign-template-preview"
          >
            <TemplatePreview
              render={renderTemplateSeguro(
                selectedTemplate.providerConfig,
                valoresDeExemplo(selectedTemplate.variables),
                selectedTemplate.variables,
              )}
              providerType={selectedTemplate.providerConfig?.providerType}
              hideHeader
              fullWidthBubble
            />
          </div>
        </div>
      )}

      {/* Variable Mapping Editor */}
      {selectedTemplate && (
        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-card-text)' }}>
            Mapeamento de Variáveis
          </label>
          <VariableMappingEditor
            variables={selectedTemplate.variables ?? []}
            variableMapping={formData.variableMapping ?? {}}
            onChange={(mapping) => updateFormData({ variableMapping: mapping })}
            availableFields={
              formData.audienceType === AudienceType.CUSTOM_AUDIENCE ? ['name'] : undefined
            }
          />
        </div>
      )}
    </FormSection>
  );
};

/**
 * Step 4: Schedule
 */
const StepSchedule: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const [sendNow, setSendNow] = useState(true);

  // Sincronizar sendNow com schedulingType (modo edição)
  useEffect(() => {
    if (formData.schedulingType === SchedulingType.SCHEDULED) {
      setSendNow(false);
    } else {
      setSendNow(true);
    }
  }, [formData.schedulingType]);

  return (
    <FormSection
      icon={'clock'}
      title="Agendamento"
      description="Quando a campanha será disparada"
    >
      <RadioGroup
        options={[
          {
            value: 'immediate',
            label: 'Enviar agora',
            description: 'Disparo inicia assim que a campanha for criada',
          },
          {
            value: 'scheduled',
            label: 'Agendar para depois',
            description: 'Escolha uma data e hora específica para o envio',
          },
        ]}
        value={sendNow ? 'immediate' : 'scheduled'}
        onChange={(val) => {
          if (val === 'immediate') {
            setSendNow(true);
            updateFormData({
              schedulingType: SchedulingType.IMMEDIATE,
              scheduledFor: undefined,
            });
          } else {
            setSendNow(false);
          }
        }}
        variant="cards"
      />

      {/* Date picker shown when scheduled */}
      {!sendNow && (
        <div className="mt-2">
          <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-card-text)' }}>
            Data e Hora do Envio <span style={{ color: 'var(--theme-error-color)' }}>*</span>
          </label>
          <div className="max-w-xs">
            <Input
              data-testid="campaign-scheduled-for"
              type="datetime-local"
              value={formData.scheduledFor || ''}
              onChange={(e) =>
                updateFormData({
                  schedulingType: SchedulingType.SCHEDULED,
                  scheduledFor: e.target.value ? e.target.value : undefined,
                })
              }
            />
          </div>
          <p className="text-xs mt-1.5" style={{ color: 'var(--theme-muted-text)' }}>
            Horário local — convertido para UTC no envio
          </p>
        </div>
      )}
    </FormSection>
  );
};

/**
 * Step 5: Review
 */
interface StepReviewProps {
  formData: Partial<CreateCampaignRequest>;
  channels: Array<{ id: string; name: string }>;
}

/** A single key/value row in the review panel. */
const ReviewRow: React.FC<{ label: string; children: React.ReactNode; first?: boolean }> = ({ label, children, first }) => (
  <div
    className="flex flex-col sm:flex-row gap-1 sm:gap-4 py-3"
    style={first ? undefined : { borderTop: '1px solid var(--theme-card-border)' }}
  >
    <span className="sm:w-40 flex-shrink-0 text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }}>
      {label}
    </span>
    <div className="flex-1 min-w-0 text-sm" style={{ color: 'var(--theme-card-text)' }}>
      {children}
    </div>
  </div>
);

const StepReview: React.FC<StepReviewProps> = ({ formData, channels }) => {
  const isCustom = formData.audienceType === AudienceType.CUSTOM_AUDIENCE;
  const { data: selectedAudience } = useAudience(isCustom ? formData.audienceId : undefined);

  // Fetch selected template (real data) to show name + mapped variable count
  const [reviewTemplate, setReviewTemplate] = useState<TemplateResponse | null>(null);
  useEffect(() => {
    let active = true;
    const loadTemplate = async () => {
      if (!formData.templateId) {
        if (active) setReviewTemplate(null);
        return;
      }
      try {
        const template = await templateService.getById(formData.templateId);
        if (active) setReviewTemplate(template);
      } catch {
        if (active) setReviewTemplate(null);
      }
    };
    loadTemplate();
    return () => { active = false; };
  }, [formData.templateId]);

  const mappedCount = Object.keys(formData.variableMapping ?? {}).length;
  const audienceBadgeLabel =
    formData.audienceType === AudienceType.LEADS
      ? 'Leads'
      : formData.audienceType === AudienceType.CONTACTS
      ? 'Contatos'
      : formData.audienceType === AudienceType.CUSTOM_AUDIENCE
      ? 'Público'
      : 'Manual';

  return (
    <FormSection
      icon={'check-circle'}
      title="Revisão"
      description="Revise os detalhes da campanha antes de salvar"
    >
      <div>
        <ReviewRow label="Nome" first>
          {formData.name || '—'}
        </ReviewRow>

        <ReviewRow label="Descrição">
          {formData.description || '—'}
        </ReviewRow>

        <ReviewRow label="Canal">
          {channels.find((c) => c.id === formData.channelId)?.name || 'Não selecionado'}
        </ReviewRow>

        <ReviewRow label="Público-Alvo">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{
                backgroundColor: 'var(--theme-primary-background)',
                color: 'var(--theme-primary)',
              }}
            >
              {audienceBadgeLabel}
            </span>
            {isCustom ? (
              <span>
                {selectedAudience
                  ? `${selectedAudience.name} (${selectedAudience.totalEntries} destinatário${selectedAudience.totalEntries !== 1 ? 's' : ''})`
                  : formData.audienceId
                  ? 'Carregando...'
                  : 'Nenhum público selecionado'}
              </span>
            ) : (
              <span>
                {formData.recipientIds?.length || 0} destinatário{(formData.recipientIds?.length || 0) !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </ReviewRow>

        <ReviewRow label="Template">
          {reviewTemplate
            ? `${reviewTemplate.name}${mappedCount > 0 ? ` · ${mappedCount} variável${mappedCount !== 1 ? 'is' : ''} mapeada${mappedCount !== 1 ? 's' : ''}` : ''}`
            : formData.templateId
            ? 'Carregando...'
            : 'Nenhum template selecionado'}
        </ReviewRow>

        <ReviewRow label="Agendamento">
          {formData.scheduledFor
            ? new Date(formData.scheduledFor).toLocaleString('pt-BR')
            : 'Enviar imediatamente'}
        </ReviewRow>
      </div>
    </FormSection>
  );
};
